// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: board geometry that does not need Excalidraw: where new things go, the words on
// a selection, and laying out a diagram Konvo sketched. Pure, so it is cheap to test and
// safe to import outside the lazily loaded canvas.

import type {BoardElementLike} from '@konvo/constants/src/BoardMerge';

/** Sticky note colours, as in the mockups: butter, blush, mint, sky, lilac. */
export const STICKY_COLORS: ReadonlyArray<string> = ['#fdf0b6', '#fbd9e1', '#cdeedd', '#d6e4fb', '#e6dcfb'];

export interface Box {
	x: number;
	y: number;
	width: number;
	height: number;
}

function num(value: unknown, fallback = 0): number {
	return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** The box around live elements, or null for an empty board. Arrows and lines count their points. */
export function sceneBounds(elements: ReadonlyArray<BoardElementLike>): Box | null {
	let minX = Number.POSITIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	for (const el of elements) {
		if (el.isDeleted) continue;
		const x = num(el.x);
		const y = num(el.y);
		const points = Array.isArray(el.points) ? (el.points as Array<[number, number]>) : null;
		if (points && points.length > 0) {
			for (const [px, py] of points) {
				minX = Math.min(minX, x + num(px));
				minY = Math.min(minY, y + num(py));
				maxX = Math.max(maxX, x + num(px));
				maxY = Math.max(maxY, y + num(py));
			}
			continue;
		}
		minX = Math.min(minX, x);
		minY = Math.min(minY, y);
		maxX = Math.max(maxX, x + num(el.width));
		maxY = Math.max(maxY, y + num(el.height));
	}
	if (!Number.isFinite(minX)) return null;
	return {x: minX, y: minY, width: maxX - minX, height: maxY - minY};
}

/** Where to put something new so it does not land on anything: to the right of the board's content. */
export function freeSpot(elements: ReadonlyArray<BoardElementLike>, gap = 80): {x: number; y: number} {
	const bounds = sceneBounds(elements);
	if (!bounds) return {x: 0, y: 0};
	return {x: bounds.x + bounds.width + gap, y: bounds.y};
}

/**
 * The words on the given elements, one entry per note or text, top to bottom then left to right.
 * A container's label counts as its own text; empty and duplicate lines are dropped.
 */
export function textOfElements(
	elements: ReadonlyArray<BoardElementLike>,
	ids: ReadonlySet<string> | null = null,
): Array<string> {
	const byId = new Map(elements.map((el) => [el.id, el]));
	const picked: Array<{text: string; x: number; y: number}> = [];
	for (const el of elements) {
		if (el.isDeleted || el.type !== 'text') continue;
		// A task card reads as its title; its status, id and initials are not notes.
		const part = (el.customData as {part?: string} | undefined)?.part;
		if (part && part !== 'title') continue;
		const containerId = typeof el.containerId === 'string' ? el.containerId : null;
		const anchor = containerId ? byId.get(containerId) : el;
		if (!anchor || anchor.isDeleted) continue;
		if (ids && !ids.has(el.id) && !(containerId && ids.has(containerId))) continue;
		const text = String(el.originalText ?? el.text ?? '')
			.replace(/\s+/g, ' ')
			.trim();
		if (text) picked.push({text, x: num(anchor.x), y: num(anchor.y)});
	}
	// Rows within ~40px read as one line of notes, left to right.
	picked.sort((a, b) => (Math.abs(a.y - b.y) > 40 ? a.y - b.y : a.x - b.x));
	return [...new Set(picked.map((entry) => entry.text))];
}

export interface SketchNode {
	id: string;
	label: string;
	shape: 'box' | 'diamond' | 'ellipse';
}

export interface SketchEdge {
	from: string;
	to: string;
	label?: string;
}

export interface PlacedNode extends SketchNode, Box {}

const NODE_SIZE: Record<SketchNode['shape'], {width: number; height: number}> = {
	box: {width: 170, height: 64},
	diamond: {width: 180, height: 100},
	ellipse: {width: 170, height: 72},
};
const GAP_X = 72;
const GAP_Y = 56;

/**
 * Lays a sketched diagram out left to right: each node goes one column after the furthest
 * node that points at it, nodes sharing a column stack downwards. Cycles are cut where they
 * close, so a loop back to the start does not push everything off to the right.
 */
export function layoutSketch(
	nodes: ReadonlyArray<SketchNode>,
	edges: ReadonlyArray<SketchEdge>,
	origin: {x: number; y: number},
): Array<PlacedNode> {
	const ids = new Set(nodes.map((node) => node.id));
	const incoming = new Map<string, Array<string>>();
	for (const edge of edges) {
		if (!ids.has(edge.from) || !ids.has(edge.to) || edge.from === edge.to) continue;
		incoming.set(edge.to, [...(incoming.get(edge.to) ?? []), edge.from]);
	}
	const column = new Map<string, number>();
	const visiting = new Set<string>();
	const columnOf = (id: string): number => {
		const known = column.get(id);
		if (known !== undefined) return known;
		if (visiting.has(id)) return 0;
		visiting.add(id);
		const parents = incoming.get(id) ?? [];
		const value = parents.length === 0 ? 0 : Math.max(...parents.map((parent) => columnOf(parent) + 1));
		visiting.delete(id);
		column.set(id, value);
		return value;
	};
	const rows = new Map<number, number>();
	const columnX = new Map<number, number>();
	const columns = nodes.map((node) => columnOf(node.id));
	const maxColumn = Math.max(0, ...columns);
	let x = origin.x;
	for (let c = 0; c <= maxColumn; c++) {
		columnX.set(c, x);
		const widest = Math.max(
			0,
			...nodes.filter((_, i) => columns[i] === c).map((node) => (NODE_SIZE[node.shape] ?? NODE_SIZE.box).width),
		);
		x += widest + GAP_X;
	}
	return nodes.map((node, i) => {
		const c = columns[i];
		const row = rows.get(c) ?? 0;
		rows.set(c, row + 1);
		const size = NODE_SIZE[node.shape] ?? NODE_SIZE.box;
		return {
			...node,
			x: columnX.get(c) ?? origin.x,
			y: origin.y + row * (NODE_SIZE.diamond.height + GAP_Y) + (NODE_SIZE.diamond.height - size.height) / 2,
			width: size.width,
			height: size.height,
		};
	});
}

/** Lines for the notes side: "- [ ] text" for each, as the editor writes to-dos. */
export function asTodoLines(texts: ReadonlyArray<string>): string {
	return texts.map((text) => `- [ ] ${text}`).join('\n');
}
